import { resolveLanguage, directionFor } from "./lib/languages";
import { readPreference, writePreference } from "./lib/preferences";
import { LanguagePicker } from "./components/LanguagePicker";
import { AdminSystemPrompt } from "./components/AdminSystemPrompt";
import { localeFor } from "./lib/languages";
import { ui, localizedCopies } from "./lib/localization";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  Cloud,
  Image,
  MessageSquareText,
  Moon,
  RefreshCw,
  ShieldCheck,
  Sun,
  Users,
  Volume2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NeuronGlyph } from "./components/ProductIcons";
import type { Language } from "./i18n";
import { IMAGE_MODELS } from "./lib/image-models";
import { FALLBACK_MODELS } from "./lib/models";
import { TTS_MODEL_IDS } from "./lib/speech";

interface DailyStats {
  day: string;
  uniqueVisitors: number;
  visits: number;
  chats: number;
  images: number;
  tts: number;
  errors: number;
}

type AiFeature = "chat" | "image" | "tts" | "browser";

interface ModelHealthRow {
  feature: AiFeature;
  modelId: string;
  requests: number;
  successes: number;
  errors: number;
  cancelled: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  toolCalls: number;
  toolSuccesses: number;
  successRate: number;
  averageDurationMs: number;
  averageFirstTokenMs: number;
  toolSuccessRate: number;
  estimatedCostUsd: number;
}

interface AdminStatsSnapshot {
  generatedAt: string;
  timezone: "UTC";
  visitors: { total: number; today: number; sevenDays: number; thirtyDays: number };
  totals: { visits: number; chats: number; images: number; tts: number; errors: number };
  modelHealth: {
    periodDays: number;
    estimatedChatCostUsd: number;
    rows: ModelHealthRow[];
  };
  daily: DailyStats[];
}

interface AdminLoadError {
  status: number;
  code?: string;
  message: string;
}

const createAdminCopy = (language: Language) => ({
  back: ui(language, "Back to workspace"),
  eyebrow: ui(language, "Site admin"),
  title: ui(language, "Model health & cost center"),
  description: ui(language, "Success rate, time to first token, full latency, tokens, and estimated cost by model for the last 30 days, plus anonymous traffic."),
  refresh: ui(language, "Refresh data"),
  language: ui(language, "Language"),
  aiRequests: ui(language, "AI model requests"),
  successRate: ui(language, "Overall success rate"),
  firstToken: ui(language, "Average first token"),
  estimatedCost: ui(language, "Estimated chat cost"),
  modelHealth: ui(language, "Model health detail"),
  modelHealthEyebrow: ui(language, "Health · 30 days"),
  model: ui(language, "Model"),
  feature: ui(language, "Type"),
  status: ui(language, "Status"),
  requests: ui(language, "Requests"),
  success: ui(language, "Success"),
  duration: ui(language, "Avg duration"),
  tokens: ui(language, "Input / output tokens"),
  tools: ui(language, "Tool calls"),
  cost: ui(language, "Est. cost"),
  healthy: ui(language, "Healthy"),
  observe: ui(language, "Observing"),
  degraded: ui(language, "Degraded"),
  incident: ui(language, "Incident"),
  chat: ui(language, "Chat"),
  image: ui(language, "Image"),
  tts: ui(language, "Speech"),
  browser: ui(language, "Web"),
  noModelData: ui(language, "The new metrics begin accumulating with this release. Complete a few generations to populate model health."),
  costHint: ui(language, "Costs use published input, cached-input, and output token prices in the model catalog. Image, speech, and the final Cloudflare bill remain authoritative in the dashboard."),
  trafficTitle: ui(language, "Anonymous traffic & feature usage"),
  trafficDescription: ui(language, "Only hashed random browser IDs and aggregate counters are stored; conversation content is not collected."),
  totalUsers: ui(language, "Unique browsers"),
  todayUsers: ui(language, "Active today"),
  weekUsers: ui(language, "Active in 7 days"),
  monthUsers: ui(language, "Active in 30 days"),
  anonymousHint: ui(language, "Deduplicated by a random browser identifier; not registered people"),
  trend: ui(language, "14-day activity"),
  uniqueUsers: ui(language, "Unique browsers"),
  visits: ui(language, "Page visits"),
  chats: ui(language, "Chat requests"),
  images: ui(language, "Images created"),
  speech: ui(language, "Speech created"),
  errors: ui(language, "Generation errors"),
  detail: ui(language, "Last 30 days"),
  date: ui(language, "Date (UTC)"),
  loading: ui(language, "Loading dashboard data…"),
  loginTitle: ui(language, "Administrator Cloudflare account required"),
  loginBody: ui(language, "Connect the Cloudflare account that owns this site. Regular user accounts cannot read these statistics."),
  login: ui(language, "Connect Cloudflare"),
  forbiddenTitle: ui(language, "This account has no dashboard access"),
  forbiddenBody: ui(language, "Switch or reconnect to the site administrator Cloudflare account from the main workspace."),
  unavailableTitle: ui(language, "The dashboard is not configured"),
  unavailableBody: ui(language, "Set the ADMIN_ACCOUNT_ID Secret and bind the METRICS_DB D1 database for this deployment."),
  errorTitle: ui(language, "Statistics are temporarily unavailable"),
  retry: ui(language, "Try again"),
  privacy: ui(language, "Only SHA-256 hashes of random browser IDs and aggregate model metrics are stored. Message content, IPs, filenames, and Cloudflare tokens are never recorded."),
  updated: (value: string) => ui(language, "Updated {0}", value),
  empty: ui(language, "Data will appear after people visit and use AI features."),
});

const copy = localizedCopies(createAdminCopy);

const modelNames = new Map<string, string>([
  ...FALLBACK_MODELS.map((model) => [model.id, model.name] as [string, string]),
  ...IMAGE_MODELS.map((model) => [model.id, model.name] as [string, string]),
  [TTS_MODEL_IDS.auraEnglish, "Aura-2 English"],
  [TTS_MODEL_IDS.auraSpanish, "Aura-2 Spanish"],
  ["browser-run/markdown", "Cloudflare Browser Run"],
  ["browser-run/screenshot", "Browser Run Screenshot"],
  ["browser-run/pdf", "Browser Run PDF"],
]);

const formatNumber = (value: number, language: Language): string =>
  Number(value || 0).toLocaleString(localeFor(language));

const formatDuration = (value: number, language: Language): string => {
  if (!value) return "—";
  if (value < 1_000) return `${Math.round(value)} ms`;
  return `${(value / 1_000).toLocaleString(localeFor(language), { maximumFractionDigits: 1 })} s`;
};

const formatCost = (value: number): string => {
  if (!value) return "$0.00";
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
};

const readAdminError = async (response: Response): Promise<AdminLoadError> => {
  const payload = await response.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
  return { status: response.status, code: payload?.error?.code, message: payload?.error?.message || `Request failed (${response.status}).` };
};

export function Admin() {
  const [language, setLanguage] = useState<Language>(() => resolveLanguage(readPreference("neurondeck-language")));
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (localStorage.getItem("neurondeck-theme-v2") as "dark" | "light" | null) ?? "light",
  );
  const [snapshot, setSnapshot] = useState<AdminStatsSnapshot | null>(null);
  const [error, setError] = useState<AdminLoadError | null>(null);
  const [loading, setLoading] = useState(true);
  const t = copy[language];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/admin/stats", { cache: "no-store" });
      if (!response.ok) throw await readAdminError(response);
      setSnapshot(await response.json() as AdminStatsSnapshot);
    } catch (loadError) {
      setError(loadError && typeof loadError === "object" && "status" in loadError
        ? loadError as AdminLoadError
        : { status: 0, message: loadError instanceof Error ? loadError.message : "Unknown error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
      "content", theme === "dark" ? "#1c1c1e" : "#f5f5f7",
    );
    document.documentElement.lang = localeFor(language);
    document.documentElement.dir = directionFor(language);
    document.title = ui(language, "Model health & cost center · NeuronDeck");
    localStorage.setItem("neurondeck-theme-v2", theme);
    writePreference("neurondeck-language", language);
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.append(robots);
    }
    robots.content = "noindex,nofollow";
  }, [language, theme]);

  const chartDays = useMemo(() => snapshot?.daily.slice(-14) ?? [], [snapshot]);
  const chartMaximum = Math.max(1, ...chartDays.map((item) => item.uniqueVisitors));
  const rows = snapshot?.modelHealth?.rows ?? [];
  const totalRequests = rows.reduce((total, row) => total + Number(row.requests || 0), 0);
  const totalSuccesses = rows.reduce((total, row) => total + Number(row.successes || 0), 0);
  const chatRows = rows.filter((row) => row.feature === "chat" && row.averageFirstTokenMs > 0);
  const weightedFirstToken = chatRows.reduce((total, row) => total + row.averageFirstTokenMs * row.requests, 0);
  const firstTokenSamples = chatRows.reduce((total, row) => total + row.requests, 0);
  const overallSuccessRate = totalRequests ? totalSuccesses / totalRequests : 0;
  const updatedAt = snapshot
    ? new Intl.DateTimeFormat(localeFor(language), {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      }).format(new Date(snapshot.generatedAt))
    : "";

  const errorContent = error ? (() => {
    if (error.status === 401) return { title: t.loginTitle, body: t.loginBody, action: t.login, login: true };
    if (error.status === 403) return { title: t.forbiddenTitle, body: t.forbiddenBody, action: t.back, login: false };
    if (error.status === 503) return { title: t.unavailableTitle, body: t.unavailableBody, action: t.back, login: false };
    return { title: t.errorTitle, body: error.message, action: t.retry, login: false };
  })() : null;
  const errorStatus = error?.status ?? 0;

  const healthLabel = (row: ModelHealthRow) => {
    if (row.requests < 3) return { label: t.observe, className: "observing" };
    if (row.successRate >= 0.98) return { label: t.healthy, className: "healthy" };
    if (row.successRate >= 0.9) return { label: t.degraded, className: "degraded" };
    return { label: t.incident, className: "incident" };
  };

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <a className="admin-brand" href="/" aria-label={t.back}><span><NeuronGlyph /></span><strong>NeuronDeck</strong><em>{t.eyebrow}</em></a>
        <div className="admin-top-actions">
          <LanguagePicker language={language} onChange={setLanguage} />
          <button type="button" onClick={() => setTheme((current) => current === "light" ? "dark" : "light")} aria-label={ui(language, "Toggle theme")}>{theme === "light" ? <Moon size={17} /> : <Sun size={17} />}</button>
          <button className="admin-refresh" type="button" onClick={() => void load()} disabled={loading}><RefreshCw className={loading ? "spinning" : ""} size={16} /><span>{t.refresh}</span></button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-heading"><div><span className="eyebrow">{ui(language, "Operations")}</span><h1>{t.title}</h1><p>{t.description}</p></div><a href="/"><ArrowLeft size={16} />{t.back}</a></div>

        {loading && !snapshot ? (
          <div className="admin-state"><Activity className="admin-state-icon spinning" /><p>{t.loading}</p></div>
        ) : errorContent ? (
          <div className="admin-state admin-error-state">
            <span className="admin-state-icon"><Cloud /></span><h2>{errorContent.title}</h2><p>{errorContent.body}</p>
            {errorContent.login ? (
              <button type="button" onClick={() => window.location.assign("/api/auth/cloudflare/start?returnTo=/admin")}>{errorContent.action}</button>
            ) : errorStatus === 0 || errorStatus >= 500 && errorStatus !== 503 ? (
              <button type="button" onClick={() => void load()}>{errorContent.action}</button>
            ) : <a href="/">{errorContent.action}</a>}
          </div>
        ) : snapshot ? (
          <>
            <AdminSystemPrompt language={language} />
            <section className="admin-stat-grid admin-health-summary" aria-label={t.modelHealth}>
              {[
                { label: t.aiRequests, value: formatNumber(totalRequests, language), icon: MessageSquareText },
                { label: t.successRate, value: totalRequests ? `${(overallSuccessRate * 100).toFixed(1)}%` : "—", icon: ShieldCheck },
                { label: t.firstToken, value: formatDuration(firstTokenSamples ? weightedFirstToken / firstTokenSamples : 0, language), icon: Activity },
                { label: t.estimatedCost, value: formatCost(snapshot.modelHealth?.estimatedChatCostUsd ?? 0), icon: Cloud },
              ].map((item) => {
                const Icon = item.icon;
                return <article className="admin-stat-card" key={item.label}><span><Icon /></span><div><small>{item.label}</small><strong>{item.value}</strong></div></article>;
              })}
            </section>

            <section className="admin-panel admin-table-panel admin-model-health">
              <div className="admin-panel-heading"><div><span className="eyebrow">{t.modelHealthEyebrow}</span><h2>{t.modelHealth}</h2></div><small>{t.updated(updatedAt)}</small></div>
              <div className="admin-table-scroll">
                <table>
                  <thead><tr><th>{t.model}</th><th>{t.feature}</th><th>{t.status}</th><th>{t.requests}</th><th>{t.success}</th><th title={t.firstToken}>TTFT</th><th>{t.duration}</th><th>{t.tokens}</th><th>{t.tools}</th><th>{t.cost}</th></tr></thead>
                  <tbody>
                    {rows.length ? rows.map((row) => {
                      const health = healthLabel(row);
                      const feature = row.feature === "chat" ? t.chat : row.feature === "image" ? t.image : row.feature === "browser" ? t.browser : t.tts;
                      return (
                        <tr key={`${row.feature}:${row.modelId}`}>
                          <td><strong className="admin-model-name">{modelNames.get(row.modelId) ?? row.modelId.split("/").at(-1)}</strong><small className="admin-model-id">{row.modelId}</small></td>
                          <td><span className={`admin-feature-badge ${row.feature}`}>{feature}</span></td>
                          <td><span className={`admin-health-badge ${health.className}`}><i />{health.label}</span></td>
                          <td>{formatNumber(row.requests, language)}</td>
                          <td>{row.requests ? `${(row.successRate * 100).toFixed(1)}%` : "—"}</td>
                          <td>{row.feature === "chat" ? formatDuration(row.averageFirstTokenMs, language) : "—"}</td>
                          <td>{formatDuration(row.averageDurationMs, language)}</td>
                          <td>{row.feature === "chat" ? `${formatNumber(row.inputTokens, language)} / ${formatNumber(row.outputTokens, language)}` : "—"}</td>
                          <td>{row.toolCalls ? `${row.toolSuccesses}/${row.toolCalls}` : "—"}</td>
                          <td>{row.feature === "chat" ? formatCost(row.estimatedCostUsd) : "—"}</td>
                        </tr>
                      );
                    }) : <tr><td className="admin-model-empty" colSpan={10}>{t.noModelData}</td></tr>}
                  </tbody>
                </table>
              </div>
              <p className="admin-cost-hint">{t.costHint}</p>
            </section>

            <div className="admin-section-intro"><span className="eyebrow">{ui(language, "Traffic")}</span><h2>{t.trafficTitle}</h2><p>{t.trafficDescription}</p></div>
            <section className="admin-stat-grid admin-visitor-grid" aria-label={t.uniqueUsers}>
              {[
                { label: t.totalUsers, value: snapshot.visitors.total, icon: Users },
                { label: t.todayUsers, value: snapshot.visitors.today, icon: Activity },
                { label: t.weekUsers, value: snapshot.visitors.sevenDays, icon: CalendarDays },
                { label: t.monthUsers, value: snapshot.visitors.thirtyDays, icon: ShieldCheck },
              ].map((item) => {
                const Icon = item.icon;
                return <article className="admin-stat-card" key={item.label}><span><Icon /></span><div><small>{item.label}</small><strong>{formatNumber(item.value, language)}</strong></div></article>;
              })}
            </section>
            <p className="admin-stat-note">{t.anonymousHint}</p>

            <div className="admin-dashboard-grid">
              <section className="admin-panel admin-trend-panel">
                <div className="admin-panel-heading"><div><span className="eyebrow">{ui(language, "Activity")}</span><h2>{t.trend}</h2></div></div>
                {chartDays.length ? (
                  <div className="admin-chart" aria-label={t.trend}>{chartDays.map((item) => <div className="admin-chart-column" key={item.day} title={`${item.day}: ${item.uniqueVisitors}`}><strong>{item.uniqueVisitors || ""}</strong><span style={{ height: `${Math.max(5, item.uniqueVisitors / chartMaximum * 100)}%` }} /><small>{item.day.slice(5).replace("-", "/")}</small></div>)}</div>
                ) : <p className="admin-empty">{t.empty}</p>}
              </section>
              <section className="admin-panel admin-usage-panel">
                <div className="admin-panel-heading"><div><span className="eyebrow">{ui(language, "Usage")}</span><h2>{t.chats}</h2></div></div>
                <div className="admin-usage-list">
                  {[
                    { label: t.visits, value: snapshot.totals.visits, icon: Activity },
                    { label: t.chats, value: snapshot.totals.chats, icon: MessageSquareText },
                    { label: t.images, value: snapshot.totals.images, icon: Image },
                    { label: t.speech, value: snapshot.totals.tts, icon: Volume2 },
                    { label: t.errors, value: snapshot.totals.errors, icon: AlertTriangle },
                  ].map((item) => { const Icon = item.icon; return <div key={item.label}><span><Icon />{item.label}</span><strong>{formatNumber(item.value, language)}</strong></div>; })}
                </div>
              </section>
            </div>

            <section className="admin-panel admin-table-panel">
              <div className="admin-panel-heading"><div><span className="eyebrow">{ui(language, "Daily")}</span><h2>{t.detail}</h2></div></div>
              <div className="admin-table-scroll"><table><thead><tr><th>{t.date}</th><th>{t.uniqueUsers}</th><th>{t.visits}</th><th>{t.chats}</th><th>{t.images}</th><th>{t.speech}</th><th>{t.errors}</th></tr></thead><tbody>{[...snapshot.daily].reverse().map((item) => <tr key={item.day}><td>{item.day}</td><td>{item.uniqueVisitors}</td><td>{item.visits}</td><td>{item.chats}</td><td>{item.images}</td><td>{item.tts}</td><td className={item.errors ? "has-errors" : ""}>{item.errors}</td></tr>)}</tbody></table></div>
            </section>
            <footer className="admin-privacy"><ShieldCheck size={16} /><p>{t.privacy}</p></footer>
          </>
        ) : null}
      </main>
    </div>
  );
}
